/**
 * Notification normalizer:
 * Converts API notifications into our internal format.
 * @see {@link https://docs.joinmastodon.org/entities/notification/}
 */
import {
  Map as ImmutableMap,
  Record as ImmutableRecord,
  fromJS,
} from 'immutable';

import type { Account, Status, EmbeddedEntity } from '@/types/entities.ts';

// https://docs.joinmastodon.org/entities/notification/
export const NotificationRecord = ImmutableRecord({
  account: null as EmbeddedEntity<Account>,
  chat_message: null as ImmutableMap<string, any> | string | null, // pleroma:chat_mention
  created_at: new Date(),
  emoji: null as string | null, // pleroma:emoji_reaction
  emoji_url: null as string | null, // pleroma:emoji_reaction
  id: '',
  name: '', // ditto:name_grant
  amount: 0, // ditto:zap
  message: '', // ditto:zap
  status: null as EmbeddedEntity<Status>,
  target: null as EmbeddedEntity<Account>, // move
  type: '',
  total_count: null as number | null, // grouped notifications
});

const normalizeType = (notification: ImmutableMap<string, any>) => {
  if (notification.get('type') === 'group_mention') {
    return notification.set('type', 'mention');
  }

  return notification;
};

/**
 * kmyblue (and similar Mastodon forks) send reaction notifications as
 * `type: "emoji_reaction"` with a nested `emoji_reaction` object, rather
 * than Pleroma's flat `emoji`/`emoji_url` fields. Map it onto the same
 * flat fields so the rest of the app doesn't need to know the difference.
 */
const normalizeEmojiReaction = (notification: ImmutableMap<string, any>) => {
  if (notification.get('type') !== 'emoji_reaction') return notification;

  const emojiReaction = notification.get('emoji_reaction');
  if (!emojiReaction) return notification;

  return notification.withMutations(notification => {
    notification.set('emoji', emojiReaction.get('name'));
    notification.set('emoji_url', emojiReaction.get('url') ?? null);
  });
};

export const normalizeNotification = (notification: Record<string, any>) => {
  return NotificationRecord(
    normalizeEmojiReaction(
      normalizeType(ImmutableMap(fromJS(notification))),
    ),
  );
};
